/**
 * ReservationsLogTab - Complete activity log tab for reservation activities
 * Features: Real-time activity tracking, advanced filtering, search, export
 */

import React, { useState, useEffect, useCallback, memo } from 'react';
import {
  ClockIcon,
  AdjustmentsHorizontalIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalendarIcon,
  UserIcon,
  CurrencyDollarIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import ReservationDetailsModal from '../../reservations.shared/components/ReservationDetailsModal';

const ReservationsLogTab = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    actionType: '',
    employeeId: '',
    customerId: '',
    dateFrom: '',
    dateTo: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  const [employees, setEmployees] = useState([]);
  const [actionTypes] = useState([
    'RESERVATION_CREATED',
    'RESERVATION_UPDATED',
    'RESERVATION_CANCELLED',
    'ITEM_CHECKED_OUT',
    'ITEM_RETURNED',
    'PAYMENT_PROCESSED',
    'REFUND_ISSUED',
    'STATUS_CHANGED'
  ]);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Format relative time
  const formatRelativeTime = useCallback((date) => {
    if (!date) return 'Unknown';
    const now = new Date();
    const activityDate = new Date(date);
    const diffInMinutes = Math.floor((now - activityDate) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
    if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)} days ago`;
    
    return activityDate.toLocaleDateString();
  }, []);

  // Format absolute time
  const formatAbsoluteTime = useCallback((date) => {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleString();
    } catch (error) {
      return 'Invalid Date';
    }
  }, []);

  // Get activity icon and color
  const getActivityIcon = useCallback((actionType) => {
    switch (actionType) {
      case 'RESERVATION_CREATED':
        return { icon: CheckCircleIcon, color: 'text-green-600', bg: 'bg-green-100' };
      case 'RESERVATION_UPDATED':
        return { icon: CalendarIcon, color: 'text-blue-600', bg: 'bg-blue-100' };
      case 'RESERVATION_CANCELLED':
        return { icon: XCircleIcon, color: 'text-red-600', bg: 'bg-red-100' };
      case 'ITEM_CHECKED_OUT':
        return { icon: ArrowDownTrayIcon, color: 'text-orange-600', bg: 'bg-orange-100' };
      case 'ITEM_RETURNED':
        return { icon: CheckCircleIcon, color: 'text-green-600', bg: 'bg-green-100' };
      case 'PAYMENT_PROCESSED':
        return { icon: CurrencyDollarIcon, color: 'text-green-600', bg: 'bg-green-100' };
      case 'REFUND_ISSUED':
        return { icon: CurrencyDollarIcon, color: 'text-yellow-600', bg: 'bg-yellow-100' };
      case 'STATUS_CHANGED':
        return { icon: ExclamationTriangleIcon, color: 'text-yellow-600', bg: 'bg-yellow-100' };
      default:
        return { icon: ClockIcon, color: 'text-gray-600', bg: 'bg-gray-100' };
    }
  }, []);

  // Format action type for display
  const formatActionType = useCallback((actionType) => {
    if (!actionType) return 'Unknown Action';
    return actionType.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  }, []);

  // Fetch employees for filter dropdown
  const fetchEmployees = useCallback(async () => {
    try {
      const response = await axios.get('/api/users', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setEmployees(response.data || []);
    } catch (error) {
      console.warn('Failed to fetch employees for filter:', error);
    }
  }, []);

  // Fetch activity logs
  const fetchActivities = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString()
      });
      
      // Add filters to params
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.trim()) {
          params.append(key, value.trim());
        }
      });
      
      const response = await axios.get(`/api/reservations/logs?${params}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = response.data;
      setActivities(data.activities || []);
      setPagination(prev => ({
        ...prev,
        page: page,
        total: data.total || 0,
        totalPages: Math.ceil((data.total || 0) / prev.limit)
      }));
      
    } catch (error) {
      console.error('Failed to fetch activity logs:', error);
      setError('Failed to load activity logs. Please try again.');
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.limit]);

  // Export activity logs
  const exportActivities = useCallback(async (format = 'csv') => {
    try {
      const params = new URLSearchParams();
      
      // Add filters to export
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.trim()) {
          params.append(key, value.trim());
        }
      });
      
      params.append('format', format);
      params.append('export', 'true');
      
      const response = await axios.get(`/api/reservations/logs/export?${params}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: format === 'csv' ? 'text/csv' : 'application/json' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `activity-log-${new Date().toISOString().split('T')[0]}.${format}`;
      link.click();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Failed to export activity logs:', error);
      alert('Failed to export activity logs. Please try again.');
    }
  }, [filters]);

  // Handle filter changes
  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      // Clear end date if start date is cleared
      if (key === 'dateFrom' && !value) {
        newFilters.dateTo = '';
      }
      return newFilters;
    });
  }, []);

  // Apply filters
  const applyFilters = useCallback(() => {
    fetchActivities(1);
  }, [fetchActivities]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters({
      actionType: '',
      employeeId: '',
      customerId: '',
      dateFrom: '',
      dateTo: '',
      search: ''
    });
    setTimeout(() => fetchActivities(1), 100);
  }, [fetchActivities]);

  // Handle reservation click - show snapshot if available, otherwise fetch current state
  const handleReservationClick = useCallback(async (activity) => {
    console.log('[ReservationsLog] 🔍 Reservation click handler called');
    console.log('[ReservationsLog] 🔍 Activity object:', activity);
    console.log('[ReservationsLog] 🔍 reservationId:', activity.reservationId);
    console.log('[ReservationsLog] 🔍 reservationNumber:', activity.reservationNumber);

    try {
      // Check if activity has a snapshot in details
      if (activity.details && activity.details.beforeSnapshot) {
        console.log('[ReservationsLog] Using snapshot data from activity:', activity.id);
        // Use the snapshot data directly
        setSelectedReservation(activity.details.beforeSnapshot);
        setShowDetailsModal(true);
      } else {
        // Fallback to fetching current reservation state
        console.log('[ReservationsLog] No snapshot available, fetching current state for:', activity.reservationId);
        console.log('[ReservationsLog] 🔍 Making API call to:', `/api/reservations/integration/${activity.reservationId}`);
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/reservations/integration/${activity.reservationId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        console.log('[ReservationsLog] 🔍 API response:', response.data);

        if (response.data?.success && response.data?.data) {
          console.log('[ReservationsLog] 🔍 Opening modal with data:', response.data.data);
          setSelectedReservation(response.data.data);
          setShowDetailsModal(true);
        } else {
          console.error('[ReservationsLog] ❌ Failed to fetch reservation details:', response.data);
          alert('Failed to load reservation details. Please try again.');
        }
      }
    } catch (error) {
      console.error('[ReservationsLog] ❌ Error fetching reservation details:', error);
      console.error('[ReservationsLog] ❌ Error response:', error.response?.data);
      alert('Failed to load reservation details. Please try again.');
    }
  }, []);

  // Close details modal
  const handleCloseDetailsModal = useCallback(() => {
    setShowDetailsModal(false);
    setSelectedReservation(null);
  }, []);

  // Initialize component
  useEffect(() => {
    fetchEmployees();
    fetchActivities();
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (!loading) {
        fetchActivities(pagination.page);
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchActivities, loading, pagination.page]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Activity Log</h2>
          <p className="text-gray-600 mt-1">
            Complete history of all reservation activities and changes
            {!loading && !error && pagination.total > 0 && (
              <span className="text-sm ml-2">({pagination.total} total entries)</span>
            )}
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center px-3 py-2 border shadow-sm text-sm leading-4 font-medium rounded-md transition-colors ${
              showFilters 
                ? 'border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100'
                : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
            }`}
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filter Logs
          </button>
          
          <button
            onClick={() => exportActivities('csv')}
            disabled={activities.length === 0}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Filter Activity Log</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder="Search activities..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            {/* Action Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Action Type
              </label>
              <select
                value={filters.actionType}
                onChange={(e) => handleFilterChange('actionType', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Actions</option>
                {actionTypes.map(type => (
                  <option key={type} value={type}>
                    {formatActionType(type)}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Employee */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Employee
              </label>
              <select
                value={filters.employeeId}
                onChange={(e) => handleFilterChange('employeeId', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Employees</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.firstName} {emp.lastName}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Date From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                placeholder="Select start date"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Shows activities for this date only (if no end date)
              </p>
            </div>

            {/* Date To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date <span className="text-gray-400 font-normal">(Optional)</span>
              </label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                min={filters.dateFrom || undefined}
                placeholder="Optional end date"
                disabled={!filters.dateFrom}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">
                {filters.dateFrom ? 'Leave empty for single date' : 'Select start date first'}
              </p>
            </div>
          </div>
          
          <div className="flex justify-between items-center">
            <button
              onClick={clearFilters}
              className="text-sm text-gray-600 hover:text-gray-800 underline"
            >
              Clear All Filters
            </button>
            
            <div className="flex space-x-3">
              <button
                onClick={applyFilters}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Active Filters Summary */}
      {(filters.dateFrom || filters.dateTo || filters.action || filters.employeeId) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="text-sm font-medium text-blue-900 mb-1">Active Filters:</h4>
              <div className="flex flex-wrap gap-2">
                {filters.dateFrom && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {filters.dateTo
                      ? `Date Range: ${new Date(filters.dateFrom).toLocaleDateString()} - ${new Date(filters.dateTo).toLocaleDateString()}`
                      : `Date: ${new Date(filters.dateFrom).toLocaleDateString()}`
                    }
                  </span>
                )}
                {filters.action && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Action: {formatActionType(filters.action)}
                  </span>
                )}
                {filters.employeeId && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Employee: {employees.find(e => e.id === filters.employeeId)?.firstName} {employees.find(e => e.id === filters.employeeId)?.lastName}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={clearFilters}
              className="text-blue-700 hover:text-blue-900 text-sm font-medium"
            >
              Clear All
            </button>
          </div>
        </div>
      )}

      {/* Activity Summary */}
      {!loading && !error && activities.length > 0 && pagination.total > 0 && (
        <div className="text-sm text-gray-600 mb-4">
          Showing {Math.max(1, (pagination.page - 1) * pagination.limit + 1)} to{' '}
          {Math.min(pagination.page * pagination.limit, Math.max(pagination.total, activities.length))} of{' '}
          {Math.max(pagination.total, activities.length)} activity log entries
        </div>
      )}

      {/* Activity Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading activities...</span>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center p-12">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-500 mr-3" />
            <span className="text-red-600">{error}</span>
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center p-12">
            <ClockIcon className="mx-auto h-16 w-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Activities Found</h3>
            <p className="text-gray-600">
              {Object.values(filters).some(f => f) 
                ? 'No activities match your current filters. Try adjusting your search criteria.'
                : 'No reservation activities have been recorded yet.'
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  TIME
                </th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  ACTION
                </th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  RESV
                </th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  EMPLOYEE
                </th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  CUSTOMER
                </th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  SERVICE
                </th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  START
                </th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                  HRS
                </th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  EXPECTED
                </th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  ACTUAL
                </th>
                <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  TOTAL
                </th>
              </tr>
            </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {activities.map((activity, index) => {
                  const { icon: IconComponent, color } = getActivityIcon(activity.action);
                  
                  // Format time for display (compact)
                  const formatTime = (date) => {
                    if (!date) return '—';
                    try {
                      return new Date(date).toLocaleString('en-US', {
                        month: 'numeric',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      });
                    } catch (error) {
                      return '—';
                    }
                  };

                  // Get action badge style
                  const getActionBadge = (action) => {
                    switch (action) {
                      case 'CREATED':
                        return 'bg-blue-100 text-blue-800';
                      case 'RETURNED':
                        return 'bg-green-100 text-green-800';
                      case 'CANCELLED':
                        return 'bg-red-100 text-red-800';
                      case 'RESTORED':
                        return 'bg-purple-100 text-purple-800';
                      case 'REPROCESSED':
                        return 'bg-yellow-100 text-yellow-800';
                      default:
                        return 'bg-gray-100 text-gray-800';
                    }
                  };
                  
                  return (
                    <tr key={activity.id || index} className="hover:bg-gray-50">
                      <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">
                        {formatTime(activity.createdAt)}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getActionBadge(activity.action)}`}>
                          {formatActionType(activity.action || '—')}
                        </span>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs">
                        {activity.reservationNumber && activity.reservationId ? (
                          <button
                            onClick={() => {
                              console.log('[ReservationsLog] 🖱️ Button clicked for reservation:', activity.reservationNumber);
                              handleReservationClick(activity);
                            }}
                            className="font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                          >
                            {activity.reservationNumber.substring(0, 8)}
                          </button>
                        ) : '—'}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900 truncate max-w-20" title={activity.actorName}>
                        {activity.actorName || '—'}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900 truncate max-w-20" title={activity.customerName}>
                        {activity.customerName || '—'}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900 truncate max-w-20" title={activity.itemLabel}>
                        {activity.itemLabel || '—'}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">
                        {formatTime(activity.startTime)}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900 text-center">
                        {activity.hours ? `${activity.hours}h` : '—'}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">
                        {formatTime(activity.expectedReturnTime)}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">
                        {formatTime(activity.actualReturnTime)}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900 text-right">
                        {activity.action === 'RETURNED' && activity.totalAmount ?
                          `$${parseFloat(activity.totalAmount).toFixed(2)}` :
                          '—'
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Pagination */}
      {!loading && !error && activities.length > 0 && pagination.total > 0 && (
        <div className="border-t border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {Math.max(1, (pagination.page - 1) * pagination.limit + 1)} to{' '}
              {Math.min(pagination.page * pagination.limit, Math.max(pagination.total, activities.length))} of{' '}
              {Math.max(pagination.total, activities.length)} results
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => fetchActivities(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeftIcon className="h-4 w-4 mr-1" />
                Previous
              </button>
              
              <span className="text-sm text-gray-700">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              
              <button
                onClick={() => fetchActivities(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRightIcon className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reservation Details Modal */}
      {showDetailsModal && selectedReservation && (
        <ReservationDetailsModal
          reservation={selectedReservation}
          onClose={handleCloseDetailsModal}
          onUpdate={() => {
            // Refresh activities after any update
            fetchActivities(pagination.page);
          }}
        />
      )}
    </div>
  );
};

export default memo(ReservationsLogTab);